import React, { useEffect, useState } from "react";
import { useAlert } from "react-common";
import { useTranslation } from "react-i18next";
import { Formik, Form, ErrorMessage } from "formik";
import { useRest, useImperativeRefresh } from "react-common";
import { Button, Typography } from "@mui/material";
import "../styles/SlideForm.css"
import { MultipleInput, SingleChoiceField, TextField, RadioList, SwitchField, Label, MarkdownField } from "ds-form";
import { template } from "../utils/commonUtils";

const SlideForm = ({ slide, setSlideConfiguration, slideConfiguration, type, setType, setTestResponses }) => {

    const { t } = useTranslation();
    const alert = useAlert();
    const { patchData } = useRest();
    const forceRefresh = useImperativeRefresh();
    const [addResponseTime, setAddResponseTime] = useState(false);
    const [addResponseNumber, setAddResponseNumber] = useState(false);

    useEffect(() => {
        if(slideConfiguration.responseTime){
            setAddResponseTime(true)
        } else {
            setAddResponseTime(false)
        }
    }, [slideConfiguration.responseTime])

    useEffect(() => {
        if(slideConfiguration.possibleResponseNumber){
            setAddResponseNumber(true);
        } else {
            setAddResponseNumber(false);
        }
    }, [slideConfiguration.possibleResponseNumber])

    const handleSubmit = (values, actions) => {
        let configurationToSend;
        switch(type) {
            case 1:
                configurationToSend = {
                    question: slideConfiguration.question,
                    possibleResponseNumber: addResponseNumber ? slideConfiguration.possibleResponseNumber : null,
                    responseTime: addResponseTime ? slideConfiguration.responseTime : null,
                };
                break;
            case 2:
                configurationToSend = {
                    question: slideConfiguration.question,
                    options: slideConfiguration.options,
                    responseTime: addResponseTime ? slideConfiguration.responseTime : null,
                    possibleResponseNumber: 1
                };
                break;
            case 3:
                configurationToSend = {
                    question: slideConfiguration.question,
                    options: slideConfiguration.options,
                    response: slideConfiguration.response,
                    responseTime: addResponseTime ? slideConfiguration.responseTime : null,
                    possibleResponseNumber: 1
                };
                break;
            case 4:
                configurationToSend = {
                    template: slideConfiguration.template,
                    content: slideConfiguration.content,
                };
                break;
            default:
                configurationToSend = {};
                break;
        }

        patchData('Slide/' + slide.id, {
            type: type,
            configuration: configurationToSend
        })
        .then(() => {
            forceRefresh('Slides');
            alert.success("Form submission succeeded");
            actions.setSubmitting(false);
        })
        .catch((e) => {
            alert.error("Form submission failed");
            actions.setSubmitting(false);
        });
    };

    const showTestResponses = (value) => {
        if(value){
            if(type === 1){
                setTestResponses([
                    {value: 'lorem', count: 7},
                    {value: 'ipsum', count: 5},
                    {value: 'dolor', count: 3},
                    {value: 'sit', count: 8},
                    {value: 'amet', count: 2},
                    {value: 'consectetur', count: 9},
                    {value: 'adipiscing', count: 4},
                    {value: 'elit', count: 6},
                    {value: 'sed', count: 3},
                    {value: 'do', count: 5},
                    {value: 'eiusmod', count: 1},
                    {value: 'tempor', count: 8},
                    {value: 'incididunt', count: 7},
                    {value: 'ut', count: 2},
                    {value: 'labore', count: 6},
                    {value: 'et', count: 4},
                    {value: 'dolore', count: 9},
                    {value: 'magna', count: 3},
                    {value: 'aliqua', count: 1},
                ])    
            } else {
                const transformedOptions = slideConfiguration.options?.map((option) => {
                    return {
                        'option': option,
                        'votes': Math.floor(Math.random() * 10) + 1 
                    };
                });
                setTestResponses(transformedOptions)
            }
            
        } else {
            setTestResponses(null)
        }
    }

    return (
        <div className="slideForm">
            <Typography variant="h6">Slide {slide.index}</Typography>
            <Formik
                enableReinitialize={true}
                initialValues={{
                    type: type,
                    configuration: slideConfiguration
                }}
                validate={(values) => {
                    let errors = {};

                    if(type !== 1 && type !== 2 && type !== 3 && type !== 4){
                        errors.configuration = t("required.type")
                    }

                    if((type === 1 || type === 2 || type === 3) && !slideConfiguration.question){
                        errors.configuration = t("required.question")
                    }

                    else if(type === 1 && addResponseNumber && !slideConfiguration.possibleResponseNumber){
                        errors.configuration = t("required.responseNumber")
                    }

                    else if((type === 2 || type === 3) && (!slideConfiguration.options || slideConfiguration.options.length < 2)){
                        errors.configuration = t("required.options")
                    }

                    else if(type === 3 && (!slideConfiguration.response || !slideConfiguration.options.includes(slideConfiguration.response.value))){
                        errors.configuration = t("required.response")
                    }

                    else if(addResponseTime && !slideConfiguration.responseTime){
                        errors.configuration = t("required.responseTime")
                    }
        
                    return errors;  
                }}
                onSubmit={handleSubmit}
            >
                <Form className="basicForm">
                    <SingleChoiceField name="type" label={t("type")}
                        options={[
                            { value: 1, label: t("slideType.wordCloud") }, 
                            { value: 2, label: t("slideType.livePolling") }, 
                            { value: 3, label: t("slideType.quiz") }, 
                            { value: 4, label: t("slideType.presentation") }
                        ]} 
                        valueAsId={true}
                        onChange={(value) => {setType(value) }}
                    />
                    
                    {(type === 1 || type === 2 || type === 3) &&
                        <TextField 
                            name="configuration.question" 
                            label={t("question")} 
                            onChange={(value) => setSlideConfiguration(prevState => ({...prevState, question: value}))}
                            inputProps={{ maxLength: 70 }}
                        />
                    }
                    
                    {type === 1 && 
                        <div>
                            <SwitchField 
                                name="configuration.possibleResponseNumber" 
                                label={t('addResponseNumber')}
                                onChange={(value) => setAddResponseNumber(value)}
                                yesLabel={t('yes')}
                                noLabel={t('no')}
                            />
                            {addResponseNumber &&
                                <TextField 
                                    name="configuration.possibleResponseNumber" 
                                    type="number" label={t("possibleResponseNumber")} 
                                    InputProps={{ inputProps: { min: 1 } }}
                                    onChange={(value) => setSlideConfiguration(prevState => ({...prevState, possibleResponseNumber: value}))}
                                />
                            }     
                        </div> 
                    } 

                    {(type === 2 || type === 3) &&
                        <MultipleInput name="configuration.options" label={t("responseOption")} value={slideConfiguration.options} 
                            onChange={(newOption) => setSlideConfiguration(prevState => ({...prevState, options: newOption}))}
                        />
                    }

                    {type === 3 && 
                        <>
                            {slideConfiguration.options?.length > 0 &&
                                <>
                                    <Label label={t('selectResponse')}/>
                                    <RadioList 
                                        value={slideConfiguration.response} 
                                        options={slideConfiguration.options.map((x) => ({ value: x, label: x }))} 
                                        onChange={(value) => setSlideConfiguration(prevState => ({...prevState, response: value}))}
                                        orientation="vertical" 
                                    />    
                                </>    
                            } 
                        </> 
                    }

                    {(type === 1 || type === 2 || type === 3) && 
                    <>
                        <SwitchField 
                            name="configuration.responseTime" 
                            label={t('addResponseTime')} 
                            onChange={(value) => setAddResponseTime(value)}
                            yesLabel={t('yes')}
                            noLabel={t('no')}
                        />

                        {addResponseTime &&
                            <TextField 
                                name="configuration.responseTime" 
                                label={t("responseTime")} 
                                type="number" 
                                InputProps={{ inputProps: { min: 1 } }}
                                onChange={(value) => setSlideConfiguration(prevState => ({...prevState, responseTime: value}))}    
                            />     
                        }
                        
                        
                    </>
                    }
                    {((type === 1) || ((type === 2 || type === 3))) &&
                        <SwitchField 
                            name="configuration.testResponses" 
                            label={t('showTestResponses')}
                            onChange={(value) => showTestResponses(value)}
                            yesLabel={t('yes')}
                            noLabel={t('no')}
                        />
                    }

                    {type === 4 && 
                        <div>
                            <SingleChoiceField name="configuration.template" label="Template" 
                                options={[
                                    { value: 1, label: "Basic Template" }, 
                                    { value: 2, label: "Template 1" },
                                    { value: 3, label: "Template 2" },
                                    { value: 4, label: "Template 3" }
                                ]} 
                                valueAsId={true}
                                onChange={(value) => {
                                    setSlideConfiguration(prevState => ({...prevState, template: value}))
                                    setSlideConfiguration(prevState => ({...prevState, content: template[value - 1].content}))
                                }}
                            />
                            <MarkdownField
                                label={t("content")}  
                                name="configuration.content" 
                                onChange={(value) => setSlideConfiguration(prevState => ({...prevState, content: value}))}
                            />
                        </div>
                    }
                    <div className="errorMessage">
                        <ErrorMessage name="configuration" />    
                    </div> 
                    <Button type="submit" variant="contained" color="primary">{t('save')}</Button>    
                </Form>
            </Formik>
        </div>
    );
};

export default SlideForm;
